import { TourBagIronDetail } from '@/api/models';
import { ILieOptionMap, ILoftOptionMap } from '@/library/LoftLieHelper';
import { ShaftLengthOptionMap } from '@/library/ShaftLengthHelper';
import { ISwingWtOptionMap } from '@/library/SwingWeightHelper';
import { IShaftOptionMap } from '@/library/ShaftHelper';
import { ShaftTippingOptionMap } from '@/library/ShaftTippingHelper';
import { IIronDynamicDropdownData, IIronStaticDropdownData } from './useEditIron';
import { IIronClub } from './IronList';
import { handFullNameToAbbreviation } from '@/library/HandHelper';

interface IBuildIronPayloadProps {
  ironClub: IIronClub;
  loftDropdownData: ILoftOptionMap | undefined;
  lieDropdownData: ILieOptionMap | undefined;
  ironStaticDropdownData: IIronStaticDropdownData | undefined;
  ironDynamicDropdownData: IIronDynamicDropdownData | undefined;
  shaftDropdownData: IShaftOptionMap | undefined;
  shaftLengthDropdownData: ShaftLengthOptionMap | undefined;
  swingWtDropdownData: ISwingWtOptionMap | undefined;
  shaftTippingData: ShaftTippingOptionMap | undefined;
}

export const buildIronPayload = ({
  ironClub,
  ironStaticDropdownData,
  ironDynamicDropdownData,
  lieDropdownData,
  loftDropdownData,
  shaftDropdownData,
  shaftLengthDropdownData,
  swingWtDropdownData,
  shaftTippingData,
}: IBuildIronPayloadProps): Partial<TourBagIronDetail> => {
  // fill non-id raw text values - ie familyName - since its part of the expected payload
  const lieData = lieDropdownData?.map[ironClub.lie];
  const loftData = loftDropdownData?.map[ironClub.loft];
  const swingWtData = swingWtDropdownData?.map[ironClub.swingWt];
  const shaftLengthData = shaftLengthDropdownData?.map[ironClub.ironShaftLength];
  const tippingData = shaftTippingData?.map[ironClub.tipping];

  return {
    // iron props
    isActive: ironClub.isActive,
    hand: handFullNameToAbbreviation(ironClub.ironHand),
    familyId: ironClub.ironFamilyId,
    familyName: (ironClub.ironFamilyId && ironStaticDropdownData?.ironFamily.map[ironClub.ironFamilyId].name) || '',
    headModelId: ironClub.ironHeadModelId,
    headModelName: (ironClub.ironHeadModelId && ironDynamicDropdownData?.ironHeadModel.map[ironClub.ironHeadModelId].name) || '',
    ironClubId: ironClub.ironClubTypeId,
    ironClubName: (ironClub.ironClubTypeId && ironStaticDropdownData?.ironClub.map[ironClub.ironClubTypeId].name) || '',
    // ironShaftId: ironClub.ironShaftId, // TODO: Request API to return shaft id as well
    shaftName: (ironClub.ironShaftId && shaftDropdownData?.map[ironClub.ironShaftId].name) || '',
    isShaftLengthFinished: ironClub.measured,
    shaftCosmetic: ironClub.ironShaftCosmetic,
    gripType: ironClub.ironGripType,
    gripSizeId: ironClub.ironGripSizeId,
    gripSize: (ironClub.ironGripSizeId && ironStaticDropdownData?.gripSize.map[ironClub.ironGripSizeId].name) || '',
    tape: ironClub.ironGripTape,
    rndRib: ironClub.ironRoundRib,
    logo: ironClub.ironLogo,
    notes: ironClub.notes,
    // loft & lie
    lieDigit: lieData?.lieDigit || '',
    lieDigitId: lieData?.joinTableDigitId,
    lieDigitId1: lieData?.sourceTableDigitId,
    lieFraction: lieData?.lieFraction || '',
    lieFractionId: lieData?.joinTableFractionId,
    lieFractionId1: lieData?.sourceTableFractionId,
    loftDigit: loftData?.loftDigit || '',
    loftDigitId: loftData?.joinTableDigitId,
    loftDigitId1: loftData?.sourceTableDigitId,
    loftFraction: loftData?.loftFraction || '',
    loftFractionId: loftData?.joinTableFractionId,
    loftFractionId1: loftData?.sourceTableFractionId,
    // swing wt
    swingWtPrefixId: swingWtData?.swingWtPrefixId,
    swingWtPrefixName: swingWtData?.swingWtPrefixName || '',
    swingWtSuffixId: swingWtData?.swingWtSuffixId,
    swingWtSuffixName: swingWtData?.swingWtSuffixName || '',
    // shaft length
    shaftLengthDigit: shaftLengthData?.digit || '',
    shaftLengthDigitId: shaftLengthData?.joinTableDigitId,
    shaftLengthDigitId1: Number(shaftLengthData?.sourceTableDigitId),
    shaftLengthFraction: shaftLengthData?.fraction || '',
    shaftLengthFractionId: Number(shaftLengthData?.joinTableFractionId),
    shaftLengthFractionId1: Number(shaftLengthData?.sourceTableFractionId),
    // final hw
    headWeight: ironClub.headWeight,
    drill: ironClub.drill,
    tipPlug: ironClub.tipPlug,
    glueWeight: ironClub.glueWeight,
    leadTape: ironClub.leadTape,
    headWeightLocationBackLookupId: ironClub.headWeightLocationBackLookupId,
    headWeightLocationBackLookupName: ironClub.headWeightLocationBackLookupName,
    headWeightNumberBackLookupId: ironClub.headWeightNumberBackLookupId,
    headWeightNumberBackLookupName: ironClub.headWeightNumberBackLookupName,

    // tipping
    shaftTippingDigit: tippingData?.digit,
    shaftTippingFraction: tippingData?.fraction,
    shaftTippingFractionId: tippingData?.joinTableFractionId,
    shaftTippingFractionId1: tippingData?.sourceTableFractionId || undefined,
  };
};
